<template>
  <!-- Library View -->
  <MainLayout
    v-scroll="onScroll"
    scroll-toolbar
    :bg-color="showWhiteBackground ? 'white' : ''"
    use-stable-scroll-gutter
    :main-layout-scroll-class="scrollClass"
  >
    <template #toolbar>
      <div class="flex flex-col w-full">
        <!-- Top Header -->
        <div class="relative flex items-center justify-between p-3 border-b border-border-normal">
          <div class="flex items-center gap-3">
            <img src="../../assets/icons/swipe-file.png" class="w-8 h-8">
            <BaseText type="label" size="sm" class="text-text-muted">
              Swipe File
            </BaseText>
          </div>

          <!-- Library Search Bar -->
          <!-- Hidden in the brands view, which searches from its filter bar instead -->
          <div v-if="['LibraryView', 'LibraryTeamView'].includes($route.name)"
          class="absolute flex top-1/2 transform -translate-y-1/2" style="left: 308px; right: 308px; z-index: 40001">
            <div class="relative flex-grow mx-auto min-w-0 h-8" style="max-width: 600px">
              <SearchBar
                :text-search.sync="textSearch"
                keywords-only
                @submit="handleSearchSubmit"
              />
            </div>
          </div>

          <!-- Create New Button -->
          <BaseCreateNewDropdown 
            primary
            @save="fetchAdvertisements(false)"
            @refresh="fetchAdvertisements(false)"
            @updateFileBoards="handleBoardsUpdateLocal" 
          />
        </div>
        <!-- Subview Tabs -->
        <div class="flex gap-2 w-full px-3 py-2" :class="{'border-b border-border-normal' : $route.name === 'LibraryMobileSavingView'}">
          <router-link :to="{ name: 'LibraryView'}" class="library-view-tab" :class="{'active': selectedTab === 'ads'}">
            <MyAdsIcon class="w-5 h-5" />
            My Ads
          </router-link>
          <router-link v-if="getTeam" :to="{ name: 'LibraryTeamView'}" class="library-view-tab" :class="{'active': selectedTab === 'team-ads'}">
            <TeamAdsIcon class="w-5 h-5" />
            Team Ads
          </router-link>
          <router-link :to="{ name: 'LibraryBrandsView'}" class="library-view-tab" :class="{'active': selectedTab === 'brands'}">
            <SwipeFileBrandsIcon class="w-5 h-5" />
            Brands
          </router-link>
          <router-link :to="{ name: 'LibraryMobileSavingView'}" class="library-view-tab" :class="{'active': selectedTab === 'mobile-saving'}">
            <MobileSavingIcon class="w-5 h-5" />
            Mobile Saving
          </router-link>
        </div>
      </div>
    </template>

    <!-- Filter Bar -->
    <template
      #filter
    >
      <div v-if="$route.name !== 'LibraryMobileSavingView'" ref="filterBar" 
      class="border border-border-normal bg-white bg-opacity-90" style="backdrop-filter: blur(6px);">
        <FilterTopBar
          v-if="showAdvertisements"
          ref="filterTopBar"
          key="main-top-bar"
          no-search
          :text-search="textSearch"
          :active-filters.sync="activeFilters"
          :selected-date-range.sync="selectedDateRange"
          :sort-order.sync="sortOrder"
          :selected-view.sync="adView"
          @change="fetchAdvertisements"
          @refresh="fetchAdvertisements"
          @toggleScrollLock="disableScroll = $event"
        />
        <FilterTopBar
          v-else
          ref="filterTopBar"
          key="secondary-top-bar"
          no-filters
          hide-unique-creatives
          :text-search.sync="textSearch"
          :favorite-filter.sync="favoriteFilter"
          @change="fetchBrands"
        />
      </div>
    </template>

    <template #content>
      <!-- Loading State -->
      <BaseLoadingLogo v-if="loadingAdvertisements" class="mt-24"/>

      <!-- Empty State -->
      <LibraryEmptyState 
        v-else-if="!advertisements.length && showAdvertisements"
        class="mt-24"
        headerText="You haven't saved any ads yet"
        subheaderText="Swipe File allows you to save, organize, and share ads from anywhere forever."
        :filters-applied="filtersApplied"
        :has-search-query="textSearch.length > 0"
        @clearFilters="clearFilters"
      />

      <!-- Advertisements List (Grid view or list view - toggled in filter bar) -->
      <AdvertisementList
        v-else-if="showAdvertisements && adView === 'grid'"
        :advertisements="advertisements"
        :loading="loadingMoreAds"
        :allAdsLoaded="allAdsLoaded"
        @updateAds="updateAds"
        @removeAds="removeAds"
        @getMoreAds="() => getMoreAds({})"
        @reload="fetchAdvertisements(false)"
      />
      <AdvertismentTableView
        v-else-if="showAdvertisements && adView === 'table'"
        :advertisements="advertisements"
        :selected-ads.sync="selectedAds"
        @updateAds="updateAds"
        @removeAds="removeAds"
        @tagDeleted="handleTagDeleteLocal"
        @update:tableViewTags="handleTagEditLocal"
        @reload="fetchAdvertisements(false)"
        @delete="hideRemovedAd"
      />

      <!-- Brands List -->
      <LibraryBrands
        v-else-if="$route.name === 'LibraryBrandsView'"
        :update-query="shouldUpdateQuery"
        :favorite-filter="favoriteFilter"
        :text-search="textSearch"
      />

      <!-- Mobile Saving View -->
      <LibraryMobileSaving
        v-else-if="$route.name === 'LibraryMobileSavingView'"
      />

      <!-- Bulk Actions Toolbar -->
      <div v-if="selectedAds.length" class="fixed bottom-nav px-4 py-3 rounded-xl flex items-center appear-animation"
      style="z-index: 30000; backdrop-filter: blur(2px);">
        <!-- Selected Number -->
        <div class="flex gap-2 text-text-muted select-none mr-4">
          <div class="rounded-full bg-background-normal w-5 h-5 flex items-center justify-center">
            <BaseText type="label" size="sm">
              {{ selectedAds.length }}
            </BaseText>
          </div>
          <BaseText type="body" size="sm">
            selected
          </BaseText>
        </div>
        <!-- Divider -->
        <div class="h-6 border-r border-border-normal mx-3" />
        <!-- Archive -->
        <BaseButton text @click="showBulkAddBoardsModal = true">
          <div class="flex items-center gap-1.5">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.03502 16.6933L4.37551 16.0251H4.37551L4.03502 16.6933ZM3.30666 15.965L3.97491 15.6245H3.97491L3.30666 15.965ZM16.6933 15.965L16.0251 15.6245L16.0251 15.6245L16.6933 15.965ZM15.965 16.6933L15.6245 16.0251L15.6245 16.0251L15.965 16.6933ZM16.6933 4.03502L16.0251 4.37551L16.0251 4.37551L16.6933 4.03502ZM15.965 3.30666L15.6245 3.97491L15.6245 3.97491L15.965 3.30666ZM3.30666 4.03502L2.6384 3.69452L2.6384 3.69452L3.30666 4.03502ZM4.03502 3.30666L3.69452 2.6384L3.69452 2.6384L4.03502 3.30666ZM10.75 3.125V2.375H9.25V3.125H10.75ZM3.125 7.375H2.375V8.875H3.125V7.375ZM9.25 16.875V17.625H10.75V16.875H9.25ZM16.875 12.625H17.625V11.125H16.875V12.625ZM16.125 5.79167V14.2083H17.625V5.79167H16.125ZM14.2083 16.125H5.79167V17.625H14.2083V16.125ZM3.875 14.2083V5.79167H2.375V14.2083H3.875ZM5.79167 3.875H14.2083V2.375H5.79167V3.875ZM5.79167 16.125C5.31258 16.125 4.99837 16.1244 4.75808 16.1048C4.52654 16.0859 4.43042 16.0531 4.37551 16.0251L3.69452 17.3616C3.99613 17.5153 4.31163 17.5733 4.63593 17.5998C4.95147 17.6256 5.33733 17.625 5.79167 17.625V16.125ZM2.375 14.2083C2.375 14.6627 2.37442 15.0485 2.4002 15.3641C2.42669 15.6884 2.48472 16.0039 2.6384 16.3055L3.97491 15.6245C3.94693 15.5696 3.91413 15.4735 3.89522 15.2419C3.87558 15.0016 3.875 14.6874 3.875 14.2083H2.375ZM4.37551 16.0251C4.20303 15.9372 4.0628 15.797 3.97491 15.6245L2.6384 16.3055C2.87009 16.7602 3.2398 17.1299 3.69452 17.3616L4.37551 16.0251ZM16.125 14.2083C16.125 14.6874 16.1244 15.0016 16.1048 15.2419C16.0859 15.4735 16.0531 15.5696 16.0251 15.6245L17.3616 16.3055C17.5153 16.0039 17.5733 15.6884 17.5998 15.3641C17.6256 15.0485 17.625 14.6627 17.625 14.2083H16.125ZM14.2083 17.625C14.6627 17.625 15.0485 17.6256 15.3641 17.5998C15.6884 17.5733 16.0039 17.5153 16.3055 17.3616L15.6245 16.0251C15.5696 16.0531 15.4735 16.0859 15.2419 16.1048C15.0016 16.1244 14.6874 16.125 14.2083 16.125V17.625ZM16.0251 15.6245C15.9372 15.797 15.797 15.9372 15.6245 16.0251L16.3055 17.3616C16.7602 17.1299 17.1299 16.7602 17.3616 16.3055L16.0251 15.6245ZM17.625 5.79167C17.625 5.33733 17.6256 4.95147 17.5998 4.63593C17.5733 4.31163 17.5153 3.99613 17.3616 3.69452L16.0251 4.37551C16.0531 4.43042 16.0859 4.52654 16.1048 4.75808C16.1244 4.99837 16.125 5.31258 16.125 5.79167H17.625ZM14.2083 3.875C14.6874 3.875 15.0016 3.87558 15.2419 3.89522C15.4735 3.91413 15.5696 3.94693 15.6245 3.97491L16.3055 2.6384C16.0039 2.48472 15.6884 2.42669 15.3641 2.4002C15.0485 2.37442 14.6627 2.375 14.2083 2.375V3.875ZM17.3616 3.69452C17.1299 3.2398 16.7602 2.87009 16.3055 2.6384L15.6245 3.97491C15.797 4.0628 15.9372 4.20303 16.0251 4.37551L17.3616 3.69452ZM3.875 5.79167C3.875 5.31258 3.87558 4.99837 3.89522 4.75808C3.91413 4.52654 3.94693 4.43042 3.97491 4.37551L2.6384 3.69452C2.48472 3.99613 2.42669 4.31163 2.4002 4.63593C2.37442 4.95147 2.375 5.33733 2.375 5.79167H3.875ZM5.79167 2.375C5.33733 2.375 4.95147 2.37442 4.63593 2.4002C4.31163 2.42669 3.99613 2.48472 3.69452 2.6384L4.37551 3.97491C4.43042 3.94693 4.52654 3.91413 4.75808 3.89522C4.99837 3.87558 5.31258 3.875 5.79167 3.875V2.375ZM3.97491 4.37551C4.06279 4.20303 4.20303 4.06279 4.37551 3.97491L3.69452 2.6384C3.2398 2.87009 2.87009 3.2398 2.6384 3.69452L3.97491 4.37551ZM9.25 3.125V8.125H10.75V3.125H9.25ZM10 7.375H3.125V8.875H10V7.375ZM9.25 8.125V11.875H10.75V8.125H9.25ZM9.25 11.875V16.875H10.75V11.875H9.25ZM10 12.625H16.875V11.125H10V12.625Z"
              fill="#5E6678" />
            </svg>
            <BaseText type="label" size="sm" class="text-text-muted">
              Add Boards
            </BaseText>
          </div>
        </BaseButton>
        <!-- Divider -->
        <div class="h-6 border-r border-border-normal mx-3" />
        <BaseButton text @click="showBulkAddTagsModal = true">
          <div class="flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <g id="tag">
                <path id="Icon" d="M16.6592 9.47812L15.8647 5.15224C15.7612 4.58899 15.3205 4.14826 14.7572 4.0448L10.4313 3.25025C9.98531 3.16833 9.5275 3.31088 9.20684 3.63155L3.62323 9.21516C3.08457 9.75381 3.08457 10.6271 3.62323 11.1658L8.74366 16.2862C9.28231 16.8249 10.1556 16.8249 10.6943 16.2862L16.2779 10.7026C16.5986 10.382 16.7411 9.92415 16.6592 9.47812Z"
                stroke="#5E6678" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path id="Icon_2" d="M12.0834 7.25C11.7152 7.25 11.4167 7.54848 11.4167 7.91667C11.4167 8.28486 11.7152 8.58333 12.0834 8.58333C12.4516 8.58333 12.7501 8.28486 12.7501 7.91667C12.7501 7.54848 12.4516 7.25 12.0834 7.25Z"
                fill="#5E6678" stroke="#5E6678" stroke-width="0.5"/>
              </g>
            </svg>
            <BaseText type="label" size="sm" class="text-text-muted">
              Add Tags
            </BaseText>
          </div>
        </BaseButton>
        <!-- Divider -->
        <div class="h-6 border-r border-border-normal mx-3" />
        <!-- Delete -->
        <BaseButton text @click="showConfirmBulkDeleteModal = true">
          <div class="flex items-center gap-1.5">
            <img src="../../assets/icons/trash.svg">
            <BaseText type="label" size="sm" class="text-secondary-red-100">
              Delete
            </BaseText>
          </div>
        </BaseButton>
      </div>

      <!-- MODALS -->
      <CreateAdvertisementModal
        v-if="showAdvertisementModal"
        :starting-tab="'extension'"
        @close="showAdvertisementModal = false"
        @save="fetchAdvertisements(false)"
        @refresh="fetchAdvertisements(false)"
        @updateFileBoards="handleBoardsUpdateLocal"
      />
      <TutorialModal
        v-if="showTutorialModal"
        @close="showTutorialModal = false"
      />
      <BulkAddBoardsModal
        v-if="showBulkAddBoardsModal"
        :selected-ads="selectedAds"
        :advertisements="advertisements"
        @updateAds="updateAds"
        @close="showBulkAddBoardsModal = false"
      />
      <BulkAddTagsModal
        v-if="showBulkAddTagsModal"
        :selected-ads="selectedAds"
        :advertisements="advertisements"
        @updateAds="updateAds"
        @tagDeleted="handleTagDeleteLocal"
        @update:tableViewTags="handleTagEditLocal"
        @close="showBulkAddTagsModal = false"
      />
      <ConfirmBulkDeleteModal
        v-if="showConfirmBulkDeleteModal === true || loadingDelete"
        :selected-ads="selectedAds"
        :loading="loadingDelete"
        @close="showConfirmBulkDeleteModal = false"
        @confirm="deleteSelectedAds"
      />

      <!-- Infinite loader -->
      <infinite-loading
        v-if="advertisements.length && !trialExpired"
        :identifier="infiniteId"
        @infinite="getMoreAds"
      >
        <div slot="spinner">
          <BaseLoadingLogo :margin="2" />
        </div>
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>
    </template>
  </MainLayout>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import ForeplayAPI from '@/api/foreplayServer'
import firebase from '@/api/config/FirebaseConfig'
import FirebaseAPI from '@/api/firebase'

// Components
import SearchBar from '../../components/search/SearchBar.vue'
import FilterTopBar from '../../components/search/FilterTopBar'
import LibraryEmptyState from '../../components/library/LibraryEmptyState.vue'

// Views
import MainLayout from '../../layouts/MainLayout'
import AdvertisementList from '../../components/advertisements/AdvertisementList'
import AdvertismentTableView from '../../components/advertisements/AdvertismentTableView.vue'
import LibraryBrands from '../../components/library/LibraryBrands'
import LibraryMobileSaving from '../../components/library/LibraryMobileSaving.vue'

// Modals
import TutorialModal from '../../components/advertisements/TutorialModal'
import BulkAddBoardsModal from '../../components/advertisements/BulkAddBoardsModal.vue'
import BulkAddTagsModal from '../../components/advertisements/BulkAddTagsModal.vue'
import ConfirmBulkDeleteModal from '../../components/globals/ConfirmBulkDeleteModal.vue'
import CreateAdvertisementModal from '../../components/advertisements/AdvertisementCreateModal'

// Icons
import MyAdsIcon from '../../components/globals/Icons/SwipeFileIcons/MyAdsIcon.vue'
import TeamAdsIcon from '../../components/globals/Icons/SwipeFileIcons/TeamAdsIcon.vue'
import SwipeFileBrandsIcon from '../../components/globals/Icons/SwipeFileIcons/SwipeFileBrandsIcon.vue'
import MobileSavingIcon from '../../components/globals/Icons/SwipeFileIcons/MobileSavingIcon.vue'

export default {
  name: 'LibraryView',
  components: {
    AdvertisementList,
    AdvertismentTableView,
    BulkAddBoardsModal,
    CreateAdvertisementModal,
    ConfirmBulkDeleteModal,
    SearchBar,
    FilterTopBar,
    LibraryEmptyState,
    LibraryBrands,
    LibraryMobileSaving,
    MainLayout,
    InfiniteLoading,
    TutorialModal,
    BulkAddTagsModal,
    MyAdsIcon,
    TeamAdsIcon,
    SwipeFileBrandsIcon,
    MobileSavingIcon
  },
  data () {
    return {
      advertisements: [],
      selectedAds: [],
      loadingMoreAds: false,
      loadingAdvertisements: false,
      allAdsLoaded: false,
      activeFilters: {},
      selectedDateRange: {
        start: null,
        end: null
      },
      lastDocId: null,
      selectedTab: '', // Initialized in mounted
      infiniteId: +new Date(),
      sortOrder: [],
      textSearch: '',
      adView: 'grid',
      disableScroll: false,

      // Bulk Actions
      showBulkAddBoardsModal: false,
      showBulkAddTagsModal: false,
      showConfirmBulkDeleteModal: false,
      loadingDelete: false,

      // Brands Section
      favoriteFilter: false,
      shouldUpdateQuery: +new Date(),

      // Modal States
      showTutorialModal: false,
      showAdvertisementModal: false,
    }
  },
  computed: {
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('MiscModule', ['getTheme']),
    ...mapGetters('RouterModule', ['getLastRoute']),
    ...mapGetters('AuthModule', ['getTeam', 'getUserFreeTrialDate', 'getUser', 'isFreeTier']),
    ...mapGetters('BriefsModule', ['getIsInspoMode']),
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('TagsModule', ['getTags']),
    filtersApplied () {
      return Object.keys(this.activeFilters).length > 0 || this.textSearch.length > 0
    },
    showAdvertisements () {
      return ['ads', 'team-ads'].includes(this.selectedTab)
    },
    showWhiteBackground () {
      return (this.showAdvertisements && this.adView === 'grid') || ['brands', 'mobile-saving'].includes(this.selectedTab)
    },
    trialExpired () {
      // check if a week has passed since trial and if they're free tier
      return (
        this.isFreeTier &&
        Date.now() > this.getUserFreeTrialDate + 6.048e8 &&
        !this.getTeam
      )
    },
    scrollClass () {
      let scrollClass = this.disableScroll ? 'overflow-y-hidden' : 'overflow-y-auto'
      scrollClass += ' overflow-x-hidden'
      return scrollClass
    }
  },
  watch: {
    adView (val) {
      localStorage.adView = val
      // Fixes a specific case when switching to table view from grid without enough ads to overflow the view
      if (val === 'table' && !this.loadingAdvertisements && !this.loadingMoreAds && this.advertisements.length < 30) {
        this.getMoreAds({})
      }
    },
    'advertisements.length' (numAds) {
      // Fixes another case similar to above but when the change happened during loading
      if (!this.loadingMoreAds && this.adView === 'table' && numAds < 30) {
        this.getMoreAds({})
      }
    }
  },
  created () {
    // Check the route query for a search term
    if (this.$route.query.search) {
      this.textSearch = this.$route.query.search
    }
  },
  async mounted () {
    // Check Ads View
    this.adView = localStorage.adView || 'grid'

    if (this.$route.query.help === 'true') {
      await window.Intercom('show')
      this.$router.push({ name: 'LibraryView' })
    }

    // Configure the subview based on the route
    if (this.$route.name === 'LibraryBrandsView') {
      this.selectedTab = 'brands'
      this.fetchBrands()
    } else if (this.$route.name === 'LibraryMobileSavingView') {
      this.selectedTab = 'mobile-saving'
    } else {
      if (this.$route.name === 'LibraryTeamView' && !this.getTeam) {
        this.$router.push({ name: 'LibraryView' })
        return
      }
      this.selectedTab = this.$route.name === 'LibraryTeamView' ? 'team-ads' : 'ads'
      this.sortOrder = this.textSearch.length
        ? [{ name: 'Most Relevant', value: 'relevance' }]
        : [{ name: 'Newest', value: 'desc' }]
      await this.fetchAdvertisements()
    }

    // Set initial top bar styles
    if (this.$refs.filterBar) {
      this.$refs.filterBar.style.position = 'sticky'
      this.$refs.filterBar.style.top = '16px'
      this.$refs.filterBar.style.zIndex = '40000'
    }
  },
  methods: {
    ...mapActions('BoardsModule', ['fetchBoards']),
    ...mapMutations('AuthModule', ['SET_USER']),
    
    // ================================================================================
    // ================================= DATA METHODS =================================
    // ================================================================================
    
    handleSearchSubmit () {
      this.updateRouteQuery()
      this.sortOrder = this.textSearch.length
        ? [{ name: 'Most Relevant', value: 'relevance' }]
        : [{ name: 'Newest', value: 'desc' }]
      if (['ads', 'team-ads'].includes(this.selectedTab)) {
        this.fetchAdvertisements()
      } else {
        this.fetchBrands()
      }
    },
    updateRouteQuery () {
      if (!this.textSearch.length) {
        window.history.replaceState({}, '', this.$route.path)
        return
      }
      // Update the route's search query parameter with the current textSearch value
      const params = new URLSearchParams(window.location.search)
      params.set('search', this.textSearch)
      const newUrl = `${this.$route.path}?${params.toString()}`
      window.history.replaceState({}, '', newUrl)
    },
    fetchBrands () {
      this.shouldUpdateQuery = +new Date()
    },
    // Fetch Advertisements
    async fetchAdvertisements (load = true) {
      this.loadingAdvertisements = load
      this.advertisements = []

      // When we're in the 'My Ads' view (default), we want to exclude ads saved by team members
      const showOnlyUserAds = this.$route.name === 'LibraryView'

      const savedBetweenStart = +new Date(this.selectedDateRange?.start) || null
      const savedBetweenEnd = +new Date(this.selectedDateRange?.end) || null
      try {
        await this.fetchBoards()

        const { results, nextPage } = await ForeplayAPI.Ads.getLibraryAds(null, {
          ...this.activeFilters,
          savedBetweenStart,
          savedBetweenEnd,
          textSearch: this.textSearch,
          sort: this.sortOrder[0].value,
          excludeTeamItems: showOnlyUserAds
        })

        this.lastDocId = nextPage

        this.advertisements = results.map((ad) => {
          // Fix deep prop watch issues
          return {
            ...ad,
            tags: ad.tags || []
          }
        })
      } catch (e) {
        console.log(e)
      } finally {
        this.loadingAdvertisements = false
        this.infiniteId += 1
      }

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 600)
      console.log('fetched ads')
    },
    hideRemovedAd(adId){
      if (!adId){
        this.$showAlert({type: 'error', message: 'Unexpected error, try refreshing the page'})
        return;
      }
      this.advertisements = this.advertisements.filter((ad) => ad?.ad_id !== adId);
    },
    async getMoreAds ($state) {
      this.loadingMoreAds = true
      if (this.trialExpired) {
        this.loadingMoreAds = false
        return
      }
      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        this.allAdsLoaded = true
        this.loadingMoreAds = false
        return
      }

      // When we're in the 'My Ads' view (default), we want to exclude ads saved by team members
      const showOnlyUserAds = this.$route.name === 'LibraryView'

      const savedBetweenStart = +new Date(this.selectedDateRange.start) || null
      const savedBetweenEnd = +new Date(this.selectedDateRange.end) || null
      const { results, nextPage } = await ForeplayAPI.Ads.getLibraryAds(this.lastDocId, {
        ...this.activeFilters,
        savedBetweenStart,
        savedBetweenEnd,
        textSearch: this.textSearch,
        sort: this.sortOrder[0].value,
        excludeTeamItems: showOnlyUserAds
      })

      this.lastDocId = nextPage

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        this.loadingMoreAds = false
        return
      }
      this.advertisements = this.advertisements.concat(results.map((ad) => {
        // Fix deep prop watch issues
        return {
          ...ad,
          tags: ad.tags || []
        }
      }))
      this.loadingMoreAds = false
      if (Object.keys($state).length) {
        $state.loaded()
      }
    },
    // Handle Clear Filters
    async clearFilters () {
      const filterTopBar = this.$refs.filterTopBar
      filterTopBar.clearAllFilters()
      this.textSearch = ''
      this.updateRouteQuery()

      await this.fetchAdvertisements()

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    },
    // Update ads
    updateAds (adsToUpdate) {
      const duplicateAds = [...this.advertisements]

      adsToUpdate.forEach((update) => {
        const index = duplicateAds.findIndex((a) => a.id === update.id)
        if (index === -1) return

        if (update.payload) {
          duplicateAds[index] = { ...duplicateAds[index], ...update.payload }
        } else {
          duplicateAds[index] = update
        }
      })

      this.advertisements = duplicateAds
      this.selectedAds = []
    },
    async deleteSelectedAds () {
      this.showConfirmBulkDeleteModal = false

      try {
        this.loadingDelete = true
        const deleteResponse = await FirebaseAPI.Advertisements.deleteAll(this.selectedAds)
        console.log(deleteResponse)

        this.$showAlert({
          type: 'success',
          message: `${deleteResponse?.deletedAdIds?.length} ads deleted successfully!`
        })

        if (deleteResponse?.newStorageUsed) {
          this.SET_USER({ ...this.getUser, storageUsed: deleteResponse.newStorageUsed })
        }

        // local update
        this.removeAds(deleteResponse?.deletedAdIds)

        this.$showAlert({
          message: 'Ads deleted successfully!',
          type: 'success'
        })

        // Remove selected ads menu
        this.selectedAds = []
      } catch (error) {
        console.log(error)
        this.$showAlert({
          message: 'Error deleting ads',
          type: 'error'
        })
      } finally {
        this.loadingDelete = false
      }
    },
    handleBoardsUpdateLocal (adId, boardIds) {
      const duplicateAds = [...this.advertisements]
      const updateIndex = duplicateAds.findIndex((ad) => ad.id === adId)
      if (updateIndex !== -1) {
        duplicateAds[updateIndex] = {
          ...duplicateAds[updateIndex],
          board_ids: boardIds
        }
      }
      this.advertisements = duplicateAds
    },
    handleTagDeleteLocal (tagId) {
      // We want the rendered list of tags to update when a tag is deleted in the EditTagsModal
      // (which can be accessed via the BulkAddTagsModal or the EditTagsModal in the AdDetailModal)
      const duplicateAds = [...this.advertisements]
      for (const ad of duplicateAds) {
        if (ad.tags) ad.tags = ad.tags.filter((tag) => tag.id !== tagId)
      }
      this.advertisements = duplicateAds
    },
    handleTagEditLocal (tagId, updatedTag) {
      // We want the rendered list of tags to update when a tag is edited in the EditTagsModal
      const duplicateAds = this.advertisements.map(ad => ({
        ...ad,
        tags: ad.tags ? ad.tags.map(tag => 
          tag.id === tagId ? {...tag, ...updatedTag} : tag
        ) : []
      }));
      this.advertisements = duplicateAds
    },
    removeAds (adsToRemove) {
      const removeIds = new Set(adsToRemove.map(adId => adId));
      this.advertisements = this.advertisements.filter(ad => !removeIds.has(ad.id));
      this.selectedAds = []
    },

    // ================================================================================
    // ================================== UI METHODS ==================================
    // ================================================================================

    moveShadowRight () {
      this.$refs.bottomShadow.classList.add('moveRight')
      this.$refs.bottomShadow.classList.remove('moveLeft')
      this.$refs.bottomShadow.classList.remove('startRight')
    },
    moveShadowLeft () {
      this.$refs.bottomShadow.classList.add('moveLeft')
      this.$refs.bottomShadow.classList.remove('moveRight')
      this.$refs.bottomShadow.classList.remove('startRight')
    },
    onScroll (e, position) {
      const marginValue =
        position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding =
        position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5

      this.$refs.filterBar.style.marginLeft = `${marginValue}px`
      this.$refs.filterBar.style.marginRight = `${marginValue}px`
      this.$refs.filterBar.style.marginTop = `${marginValue}px`
      this.$refs.filterBar.style.borderRadius = `${rounding}px`

      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue
          ? position.scrollTop / 15
          : maxValue
      }

      if (position.scrollTop) {
        this.$refs.filterBar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(
        15
      )}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
        7
      )}px ${shadowSection(
        10
      )}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(
        7
      )}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
        3
      )}px ${shadowSection(5)}px`
      // this.$refs.filterBar.style.boxShadow = `rgba(58, 111, 251, 0.4) -${shadowSection(5)}px ${shadowSection(5)}px, rgba(58, 111, 251, 0.3) -${shadowSection(10)}px ${shadowSection(10)}px, rgba(58, 111, 251, 0.2) -${shadowSection(15)}px ${shadowSection(15)}px, rgba(58, 111, 251, 0.1) -${shadowSection(20)}px ${shadowSection(20)}px, rgba(58, 111, 251, 0.05) -${shadowSection(25)}px ${shadowSection(25)}px`
      } else {
        this.$refs.filterBar.style.boxShadow = null
      }
    },
    showIntercom () {
      window.Intercom('show')
    },
  },
  metaInfo: {
    title: 'Foreplay.co | Swipe File'
  }
}
</script>

<style scoped>
.library-view-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none; /* Standard */
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
  white-space: nowrap;
}
.library-view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.library-view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
  font-weight: 500;
}

.shadow-elem {
  display: flex;
  justify-content: center;
  margin-left: 5%;
  width: 40%;
}
.moveRight {
  animation: moveRightAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}
.moveLeft {
  animation: moveLeftAnimation 0.5s ease-out;
  animation-fill-mode: forwards;
}
.startRight {
  transform: translateX(50%);
}
@keyframes moveRightAnimation {
  from {transform: translateX(0%);}
  to {transform: translateX(50%);}
}
@keyframes moveLeftAnimation {
  from {transform: translateX(50%);}
  to {transform: translateX(0%);}
}

.bottom-nav {
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0px 8px 24px -12px rgba(2, 23, 59, 0.36),
    0px 1px 2px rgba(164, 172, 185, 0.16),
    0px 0px 0px 1px rgba(18, 55, 105, 0.08);
  backdrop-filter: blur(5px);

  z-index: 57;
  transform: translateX(-50%);

  /* 138 is half of the sidebar width */
  left: calc(50% + 138px);
  bottom: 38px;

  border-radius: 12px;
}
.appear-animation {
  animation: appear 300ms ease-in;
  animation-iteration-count: 1;
}
@keyframes appear {
  0% {
    opacity: 0;
    transform: translate(-50%, 20px);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

/* Vue <transition> classes */
.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>
